/**
 * livekitKeyProvider.js — Government Shield's adapter around
 * LiveKit's `ExternalE2EEKeyProvider` (livekit-client v2.x).
 *
 * **Why this file exists.** LiveKit ships a built-in passphrase-
 * based key exchange. We do not use it. Instead, every key that
 * goes into `ExternalE2EEKeyProvider.setKey(...)` is derived from
 * a 32-byte AES-GCM value that was generated locally, wrapped per-
 * participant with the existing Sealed-Sender RSA-OAEP + Kyber-768
 * hybrid, and shipped to the peers over the already-audited
 * messaging channel. LiveKit's server never sees plaintext key
 * material; its built-in key-exchange path is left unused.
 *
 * Phase 2 scope (plumbing only — no UI, no call wiring):
 *   - createSealedE2EEKeyProvider() — factory returning a raw
 *     ExternalE2EEKeyProvider. Opinionated defaults: AES-GCM-256,
 *     ratchet-salt namespaced to "gs-voice-sfu-v1".
 *   - installPassphrase(provider, passphrase) — thin async wrapper
 *     around .setKey() with the right type coercion (v2 accepts
 *     either a string or an ArrayBuffer; we always pass ArrayBuffer
 *     so HKDF is used instead of PBKDF2).
 *   - deriveSFrameSAS(keyBytes) — SAS fingerprint helper. Mirrors
 *     the DTLS-based one in utils/voiceSAS.js. Same 6-digit "NNN-
 *     NNN" format so the UI can display it identically.
 *   - randomRoomKey() — 32 bytes from Web Crypto. Caller is
 *     responsible for wrapping + distributing via Sealed Sender.
 *
 * Phase 3 will wire this into VoiceCallModal when
 * participants.length + 1 >= LIVEKIT_GROUP_CALL_THRESHOLD.
 */

import { ExternalE2EEKeyProvider } from 'livekit-client';

/**
 * Namespace for HKDF ratchets. Bumped on every breaking change to
 * the key-distribution protocol so old clients don't silently
 * decrypt new streams with stale parameters.
 */
export const GS_VOICE_RATCHET_SALT = 'gs-voice-sfu-v1';

/**
 * Size (in bits) of the SFrame encryption keys. 256 is supported
 * by LiveKit's JS client; mobile SDKs currently cap at 128. We're
 * web-only in Phase 2 so 256 is fine.
 */
export const SFRAME_KEY_SIZE = 256;

/**
 * Length of the raw AES-GCM key material we generate + wrap +
 * ship via Sealed Sender. 32 bytes = 256-bit key.
 */
export const RAW_KEY_BYTES = 32;

/**
 * Build a configured ExternalE2EEKeyProvider. One per Room —
 * disposing the Room disposes the provider.
 *
 * `sharedKey: true` means LiveKit uses a single passphrase for
 * every participant's frames. That's what we want: the entire
 * room derives a common SFrame key from a single 32-byte secret
 * that the initiator generates, wraps per-peer via Sealed
 * Sender, and distributes. When a peer joins mid-call we
 * rotate (new 32 bytes → new wrapped payload per peer →
 * provider.setKey(newBytes) on every client).
 */
export const createSealedE2EEKeyProvider = () => {
  return new ExternalE2EEKeyProvider({
    ratchetSalt: GS_VOICE_RATCHET_SALT,
    keySize: SFRAME_KEY_SIZE,
    // ratchetWindowSize / failureTolerance / keyringSize left at
    // library defaults. Changing them is a deliberate upgrade
    // that requires a spec-level version bump on the salt above.
  });
};

/**
 * Hand a 32-byte shared secret to the provider.
 *
 * We always pass an ArrayBuffer (not a string) because the v2 API
 * takes the HKDF path for ArrayBuffer input and PBKDF2 for
 * strings. HKDF is the right primitive when the input is already
 * high-entropy random bytes — PBKDF2's iteration count is a
 * brute-force defence that's pointless over random material.
 *
 * `raw` must be exactly RAW_KEY_BYTES long. Anything else is a
 * wire-format bug upstream and we refuse rather than silently
 * truncate/pad to a shorter key that would still "work" but
 * derive a predictable downstream ratchet.
 */
export const installPassphrase = async (provider, rawBytes) => {
  if (!provider || typeof provider.setKey !== 'function') {
    throw new Error('livekitKeyProvider: invalid provider');
  }
  const buf = _asArrayBuffer(rawBytes);
  if (buf.byteLength !== RAW_KEY_BYTES) {
    throw new Error(
      `livekitKeyProvider: expected ${RAW_KEY_BYTES}-byte key, got ${buf.byteLength}`,
    );
  }
  await provider.setKey(buf);
};

/**
 * Derive the Short-Authentication-String (SAS) code for a voice
 * room, given the raw 32-byte shared secret every participant
 * installed. Hash-based derivation binds the spoken code to the
 * actual SFrame key — a MITM SFU would need a different key to
 * decrypt, therefore would derive a different SAS, therefore
 * would be caught when the two humans read the code aloud.
 *
 * Mirrors the 6-digit "NNN-NNN" format used by the DTLS-based
 * SAS in utils/voiceSAS.js so the existing SAS UI can render
 * SFU-derived codes without a shape change.
 *
 * Domain-separated under the label "gs-voice-sas-sfu-v1" to
 * prevent anyone pre-computing a table that maps a random
 * 32-byte secret to its code — the salt has to be baked in.
 */
export const deriveSFrameSAS = async (rawBytes) => {
  if (!rawBytes) return null;
  const buf = _asArrayBuffer(rawBytes);
  if (buf.byteLength !== RAW_KEY_BYTES) return null;

  const labelBuf = new TextEncoder().encode('|gs-voice-sas-sfu-v1');
  const concat = new Uint8Array(buf.byteLength + labelBuf.byteLength);
  concat.set(new Uint8Array(buf), 0);
  concat.set(labelBuf, buf.byteLength);

  const digest = await crypto.subtle.digest('SHA-256', concat);
  const view = new DataView(digest);
  const n = view.getUint32(0, false) % 1_000_000;
  const s = String(n).padStart(6, '0');
  return `${s.slice(0, 3)}-${s.slice(3)}`;
};

/**
 * Generate RAW_KEY_BYTES of cryptographically random bytes for
 * a new room secret. Returned as Uint8Array so the caller can
 * feed it straight to their existing Sealed-Sender wrap helpers
 * without a copy.
 *
 * Separate from installPassphrase() so tests can feed in a known
 * fixed value and assert determinism on deriveSFrameSAS().
 */
export const randomRoomKey = () => {
  const out = new Uint8Array(RAW_KEY_BYTES);
  crypto.getRandomValues(out);
  return out;
};

/**
 * Accept Uint8Array | ArrayBuffer | Array<number>, normalise to
 * ArrayBuffer. The LiveKit v2 setKey() API expects an
 * ArrayBuffer; anything else throws a confusing internal error.
 */
const _asArrayBuffer = (x) => {
  if (x instanceof ArrayBuffer) return x;
  if (ArrayBuffer.isView(x)) {
    // Slice out the live view window — subarrays share memory
    // with their backing ArrayBuffer, which can accidentally
    // carry 100KB of siblings into the worker.
    return x.buffer.slice(x.byteOffset, x.byteOffset + x.byteLength);
  }
  if (Array.isArray(x)) {
    return new Uint8Array(x).buffer;
  }
  throw new Error('livekitKeyProvider: key must be ArrayBuffer / Uint8Array / number[]');
};
